import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Factory, FlaskConical, Beaker, Package, Tags, Boxes, Layers, Rocket, MessageCircle, ArrowRight, CheckCircle2, Loader2, Send, Handshake, Globe2, Droplets, Scissors, HeartPulse, Star, Crown, Wrench } from 'lucide-react';
import Seo from '../components/Seo';
import useReveal from '../hooks/useReveal';
import { useLang } from '../context/LanguageContext';
import { IMAGES } from '../mock/mock';
import { WHATSAPP_URL, track } from '../components/landingUtils';
import { PhoneInput, COUNTRIES, validatePhone } from '../components/PhoneInput';
import { getLandingCopy } from '../i18n/landing';
import CaseStudies from '../components/CaseStudies';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CAP_ICONS = [FlaskConical, Beaker, Boxes, Factory, Droplets, Tags, Package, Layers, Rocket];
const CARD_ICONS = [Droplets, Scissors, HeartPulse, Star, Crown, Beaker, Tags, Wrench];

const Kicker = ({ children, light }) => (
  <span className={`inline-flex items-center gap-3 text-[0.7rem] tracking-[0.35em] uppercase ${light ? 'text-gold-light' : 'text-gold'}`}>
    <span className="w-8 h-px bg-gold" />{children}<span className="w-8 h-px bg-gold" />
  </span>
);

const initial = { name: '', company: '', email: '', phone: '', country: '', category: '', quantity: '', description: '' };
const field = 'w-full bg-transparent border-b border-espresso/20 py-3 text-espresso placeholder-mocha/50 focus:outline-none focus:border-gold transition-colors';

export default function ContractManufacturing() {
  const { lang } = useLang();
  const L = getLandingCopy(lang);
  const c = L.cm, sh = L.shared;
  useReveal(lang);
  const [form, setForm] = useState(initial);
  const [phoneCountry, setPhoneCountry] = useState('GB');
  const [status, setStatus] = useState('idle');
  const [errMsg, setErrMsg] = useState('');
  useEffect(() => { track('landing_page_view', { page: 'contract-cosmetics-manufacturing' }); }, []);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrMsg('');
    const digits = form.phone.replace(/\D/g, '');
    if (digits && !validatePhone(digits)) { setErrMsg(sh.errPhone); return; }
    const dial = (COUNTRIES.find((x) => x.iso === phoneCountry) || {}).dial || '44';
    setStatus('sending');
    try {
      await axios.post(`${API}/contact`, {
        name: form.name, email: form.email, phone: digits ? `+${dial} ${digits}` : '', company: form.company,
        subject: `Manufacturing Consultation — ${form.category || 'General'}`,
        message: `Country: ${form.country}\nProduct category: ${form.category}\nEstimated quantity / MOQ: ${form.quantity}\n\nProject description:\n${form.description}`,
      });
      track('quote_request', { page: 'contract-manufacturing' });
      track('contact_form_submit', { page: 'contract-manufacturing' });
      setStatus('success');
    } catch (err) {
      setStatus('idle');
      setErrMsg(sh.errGeneric);
    }
  };

  return (
    <div>
      <Seo title="Contract Cosmetics Manufacturing UK"
        description="ARGE Cosmetics provides professional contract cosmetics manufacturing, formulation, filling, packaging and turnkey production for beauty brands, retailers, clinics and entrepreneurs."
        path="/contract-cosmetics-manufacturing"
        jsonLd={{ '@context': 'https://schema.org', '@type': 'Service', name: 'Contract Cosmetics Manufacturing', provider: { '@type': 'Organization', name: 'ARGE Cosmetics', url: 'https://argecosmetics.co.uk' }, areaServed: 'GB', serviceType: 'Contract cosmetics manufacturing' }} />

      <section className="relative min-h-[88vh] flex items-center pt-32 pb-20 overflow-hidden bg-espresso">
        <img src={IMAGES.manufacturing} alt="Professional cosmetics manufacturing facility" className="absolute inset-0 w-full h-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-r from-espresso via-espresso/85 to-espresso/30" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full">
          <div className="max-w-2xl">
            <Kicker light>{c.kicker}</Kicker>
            <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory leading-[1.08]">{c.h1}</h1>
            <p className="mt-6 text-lg text-ivory/85 leading-relaxed max-w-xl">{c.sub}</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a href="#consultation" onClick={() => track('quote_request', { page: 'contract-hero' })} data-testid="cm-hero-primary-cta" className="gold-sheen inline-flex items-center gap-3 px-10 py-4 bg-gold text-espresso text-xs tracking-[0.2em] uppercase font-medium hover:bg-gold-light transition-colors">{c.cta1} <ArrowRight className="w-4 h-4 rtl:rotate-180" /></a>
              <Link to="/contact" onClick={() => track('quote_request', { page: 'contract-hero-secondary' })} data-testid="cm-hero-secondary-cta" className="inline-flex items-center gap-3 px-10 py-4 border border-ivory/30 text-ivory text-xs tracking-[0.2em] uppercase hover:border-gold-light hover:text-gold-light transition-colors">{sh.quote}</Link>
            </div>
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" onClick={() => track('whatsapp_click', { page: 'contract' })} data-testid="cm-whatsapp-link" className="mt-6 inline-flex items-center gap-2 text-sm text-ivory/70 hover:text-gold-light transition-colors"><MessageCircle className="w-4 h-4 text-gold-light" /> {sh.wa}</a>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-ivory">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14 reveal">
            <Kicker>{c.e2eK}</Kicker>
            <h2 className="font-serif text-3xl md:text-5xl text-espresso mt-4">{c.e2eT}</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {c.caps.map((cp, i) => {
              const Icon = CAP_ICONS[i];
              return (
                <div key={cp} className="reveal flex items-center gap-3 bg-cream px-6 py-5 border-l-2 border-gold">
                  <Icon className="w-5 h-5 text-gold shrink-0" />
                  <span className="text-sm text-espresso">{cp}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-cream overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14 reveal">
            <Kicker>{c.procK}</Kicker>
            <h2 className="font-serif text-3xl md:text-5xl text-espresso mt-4">{c.procT}</h2>
          </div>
          <div className="reveal flex flex-wrap justify-center items-center gap-y-6">
            {c.timeline.map((step, i) => (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center px-2">
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-espresso text-gold-light font-serif text-sm">{i + 1}</span>
                  <span className="mt-2 text-[0.68rem] tracking-[0.12em] uppercase text-espresso text-center max-w-[110px]">{step}</span>
                </div>
                {i < c.timeline.length - 1 && <span className="hidden md:block w-8 h-px bg-gold mx-1 mb-6" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-espresso text-ivory">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14 reveal">
            <Kicker light>{c.makeK}</Kicker>
            <h2 className="font-serif text-3xl md:text-5xl mt-4">{c.makeT}</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {c.cards.map((cd, i) => {
              const Icon = CARD_ICONS[i];
              return (
                <div key={cd} className="reveal bg-ivory/5 border border-gold/25 p-7 text-center hover:border-gold transition-colors">
                  <Icon className="w-7 h-7 text-gold-light mx-auto mb-4" />
                  <h3 className="font-serif text-lg leading-snug">{cd}</h3>
                </div>
              );
            })}
          </div>
          <p className="mt-10 text-center text-sm text-ivory/60 reveal">{c.crossPre}<Link to="/private-label-cosmetics" className="text-gold-light underline underline-offset-4 hover:text-ivory transition-colors">{c.crossL1}</Link>{c.crossPost}</p>
        </div>
      </section>

      <section className="py-20 md:py-24 bg-ivory">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div className="reveal">
            <Kicker>{c.growK}</Kicker>
            <h2 className="font-serif text-3xl md:text-4xl text-espresso mt-4">{c.growT}</h2>
            <ul className="mt-8 space-y-4">
              {c.growth.map((g) => (
                <li key={g} className="flex items-start gap-3 text-mocha"><CheckCircle2 className="w-5 h-5 text-gold shrink-0 mt-0.5" />{g}</li>
              ))}
            </ul>
          </div>
          <div className="reveal">
            <img src={IMAGES.lab} alt="ARGE Cosmetics laboratory and product development" loading="lazy" className="w-full h-[420px] object-cover" />
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14 reveal">
            <Kicker>{c.whyK}</Kicker>
            <h2 className="font-serif text-3xl md:text-5xl text-espresso mt-4">{c.whyT}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {c.why.map((wp) => (
              <div key={wp.t} className="reveal bg-ivory p-7 border-t-2 border-gold">
                <Handshake className="w-6 h-6 text-gold mb-4" />
                <h3 className="font-serif text-xl text-espresso leading-snug">{wp.t}</h3>
                <p className="mt-2 text-sm text-mocha leading-relaxed">{wp.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CaseStudies />

      <section id="consultation" className="py-20 md:py-28 bg-espresso">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-10 reveal">
            <Kicker light>{c.formK}</Kicker>
            <h2 className="font-serif text-3xl md:text-5xl text-ivory mt-4">{c.formT}</h2>
            <p className="mt-4 text-ivory/70 text-sm">{c.formNote}</p>
          </div>
          {status === 'success' ? (
            <div className="text-center bg-ivory p-12 border-t-2 border-gold" data-testid="cm-form-success">
              <CheckCircle2 className="w-12 h-12 text-gold mx-auto mb-4" />
              <h3 className="font-serif text-3xl text-espresso">{c.okT}</h3>
              <p className="mt-3 text-mocha">{c.okP}</p>
              <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" onClick={() => track('whatsapp_click', { page: 'contract-success' })} className="mt-6 inline-flex items-center gap-2 text-sm text-gold hover:text-espresso transition-colors"><MessageCircle className="w-4 h-4" /> {c.okWa}</a>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="bg-ivory p-8 md:p-12 border-t-2 border-gold" data-testid="cm-consultation-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <input required name="name" value={form.name} onChange={onChange} placeholder={c.phName} className={field} data-testid="cm-name" />
                <input name="company" value={form.company} onChange={onChange} placeholder={c.phCompany} className={field} data-testid="cm-company" />
                <input required type="email" name="email" value={form.email} onChange={onChange} placeholder={c.phEmail} className={field} data-testid="cm-email" />
                <PhoneInput country={phoneCountry} onCountry={setPhoneCountry} value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} placeholder={c.phPhone} />
                <select name="country" value={form.country} onChange={onChange} className={`${field} cursor-pointer`} data-testid="cm-country">
                  <option value="">{c.phCountry}</option>
                  {COUNTRIES.map((x) => <option key={x.iso} value={x.name}>{x.name}</option>)}
                </select>
                <select required name="category" value={form.category} onChange={onChange} className={`${field} cursor-pointer`} data-testid="cm-category">
                  <option value="">{c.phCategory}</option>
                  {c.categories.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <input name="quantity" value={form.quantity} onChange={onChange} placeholder={c.phQty} className={`${field} mt-6`} data-testid="cm-quantity" />
              <textarea required name="description" value={form.description} onChange={onChange} rows={5} placeholder={c.phDesc} className={`${field} mt-6 resize-none`} data-testid="cm-description" />
              {errMsg && <p className="mt-4 text-sm text-destructive" data-testid="cm-error">{errMsg}</p>}
              <button type="submit" disabled={status === 'sending'} data-testid="cm-submit"
                className="group mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 px-10 py-4 bg-espresso text-ivory text-xs tracking-[0.2em] uppercase hover:bg-gold transition-colors disabled:opacity-70">
                {status === 'sending' ? <><Loader2 className="w-4 h-4 animate-spin" /> {sh.sending}</> : <>{c.submit} <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform rtl:rotate-180" /></>}
              </button>
            </form>
          )}
          <p className="mt-8 text-center text-xs text-ivory/50 flex items-center justify-center gap-2 reveal"><Globe2 className="w-4 h-4 text-gold-light" /> {c.trust}</p>
        </div>
      </section>
    </div>
  );
}
